import React, { useEffect, useMemo, useState } from "react";
import { useApp } from "../../../state/Context";
import {
  getCatalog,
  markDailyVisit,
  flushPendingStats,
} from "../../../firebase/functions";
import { StarIcon } from "../../primatives/Icons";

const PINNED_IDS = new Set(["0", "47"]);

const DEGREE_LEVELS = [
  { key: "bachelors", title: "Bachelor’s", subtitle: "Undergraduate (BA/BSc/BEng)" },
  { key: "integrated", title: "Integrated Master’s", subtitle: "Undergraduate + Master’s (MEng/MSci)" },
  { key: "masters", title: "Master’s", subtitle: "Postgraduate Taught (MSc/MA)" },
  { key: "phd", title: "PhD", subtitle: "Doctorate / Research" },
];

function cx(...parts) {
  return parts.filter(Boolean).join(" ");
}

function StepPill({ children }) {
  return (
    <span className="inline-flex items-center rounded-md bg-neutral-200 px-2 py-1 text-xs font-semibold text-neutral-700 dark:bg-neutral-800 dark:text-neutral-200">
      {children}
    </span>
  );
}

function Card({ children }) {
  return (
    <div className="rounded-2xl border border-neutral-200 bg-white p-6 shadow-sm dark:border-neutral-800 dark:bg-neutral-900">
      {children}
    </div>
  );
}

function NoticeBanner() {
  const { setSelection } = useApp();

  return (
    <div className="mt-4 relative overflow-hidden rounded-2xl border border-indigo-300 bg-indigo-50 p-4 text-sm shadow-md dark:border-indigo-700 dark:bg-indigo-950">
      <div className="absolute inset-y-0 left-0 w-1.5 bg-indigo-500" />

      <div className="flex flex-col gap-3 pl-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <div className="flex items-center gap-2">
            <span className="rounded-md bg-indigo-600 px-2 py-0.5 text-xs font-bold text-white">
              NEW
            </span>
            <span className="font-semibold text-indigo-900 dark:text-indigo-200">
              Early Version Access
            </span>
          </div>

          <p className="mt-1 text-indigo-800 dark:text-indigo-300">
            You’re using an early version of the platform. Features and universities are still being added. 
          </p>

          <p className="text-indigo-700 dark:text-indigo-400">
            See what’s coming next on the roadmap.
          </p>
        </div>

        <button
          type="button"
          onClick={() => setSelection((p) => ({ ...p, step: "timeline" }))}
          className="inline-flex items-center justify-center rounded-xl bg-indigo-600 px-4 py-2 text-xs font-semibold text-white shadow-sm transition hover:bg-indigo-700 active:scale-[0.98]"
        >
          View Roadmap →
        </button>
      </div>
    </div>
  );
}

export default function SelectScreen() {
  const { selection, setSelection } = useApp();
  const [query, setQuery] = useState("");

  const [sortOrder, setSortOrder] = useState("none"); // "none" | "az" | "za"
  const [levelFilter, setLevelFilter] = useState(null); // null | "bachelors" | "masters" | "integrated" | "phd"

  const [universities, setUniversities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");

  const selectedUni = selection.university;
  const selectedLevel = selection.degreeLevel;

  useEffect(() => {
    markDailyVisit();

    const flush = () => flushPendingStats();

    window.addEventListener("pagehide", flush);
    const onVis = () => {
      if (document.visibilityState === "hidden") flush();
    };
    document.addEventListener("visibilitychange", onVis);

    return () => {
      window.removeEventListener("pagehide", flush);
      document.removeEventListener("visibilitychange", onVis);
    };
  }, []);

  useEffect(() => {
    let alive = true;

    (async () => {
      try {
        setLoading(true);
        setLoadError("");
        const data = await getCatalog();
        if (!alive) return;

        setUniversities(Array.isArray(data?.universities) ? data.universities : []);
      } catch {
        if (!alive) return;
        setLoadError("Failed to load universities. Please try again.");
        setUniversities([]);
      } finally {
        if (alive) setLoading(false);
      }
    })();

    return () => {
      alive = false;
    };
  }, []);

  const isSearching = query.trim().length > 0;

  const uniSupportsLevel = (u, levelKey) =>
    !levelKey ||
    (Array.isArray(u.enabledDegreeTypes) && u.enabledDegreeTypes.includes(levelKey));

  const pinnedIds = useMemo(() => {
    if (isSearching) return new Set(); // disable pinned during search
    return PINNED_IDS;
  }, [isSearching]);

  const filteredUniversities = useMemo(() => {
    const q = query.trim().toLowerCase();

    // 1) search
    let base = !q ? universities : universities.filter((u) => (u.name || "").toLowerCase().includes(q));

    // 2) degree filter
    base = base.filter((u) => uniSupportsLevel(u, levelFilter));

    // 3) pinned split (only when NOT searching)
    let pinned = [];
    let rest = base;

    if (!isSearching) {
      pinned = [];
      rest = [];
      for (const u of base) {
        (pinnedIds.has(String(u.id)) ? pinned : rest).push(u);
      }
    }

    // 4) sorting
    const collator = new Intl.Collator(undefined, { sensitivity: "base", numeric: true });
    const sortFn =
      sortOrder === "az"
        ? (a, b) => collator.compare(a?.name || "", b?.name || "")
        : sortOrder === "za"
        ? (a, b) => collator.compare(b?.name || "", a?.name || "")
        : null;

    if (sortFn) {
      pinned = [...pinned].sort(sortFn);
      rest = [...rest].sort(sortFn);
    }

    return !isSearching ? [...pinned, ...rest] : rest;
  }, [query, universities, isSearching, pinnedIds, sortOrder, levelFilter]);

  // NEW: total count label (match what user is viewing, i.e., after search/filter)
  const totalUniversities = useMemo(
    () => (loading ? 0 : filteredUniversities.length),
    [loading, filteredUniversities.length]
  );

  const chooseUniversity = (u) => {
    setSelection((prev) => ({
      ...prev,
      university: u,
      degreeLevel: null,
    }));
  };

  const chooseDegreeLevel = (levelKey) => {
    setSelection((prev) => ({
      ...prev,
      degreeLevel: levelKey,
    }));
  };

  const canContinue = Boolean(selectedUni && selectedLevel);
  const enabledForUni = selectedUni?.enabledDegreeTypes || [];

  const continueToCalc = () => {
    if (!canContinue) return;
    setSelection((prev) => ({ ...prev, step: "calc" }));
  };

  // Scroll behavior: always reserve scrollbar space; disable scrolling if not needed.
  const shouldScroll =!loading && filteredUniversities.length > 6; 

  return (
    <div className="mx-auto max-w-4xl space-y-6">
      <NoticeBanner />

      {/* Title */}
      <div>
        <h1 className="text-xl font-semibold tracking-tight text-neutral-900 dark:text-neutral-50 sm:text-2xl">
          Get started
        </h1>
        <p className="mt-1 text-sm text-neutral-600 dark:text-neutral-400">
          Select your university and degree level to continue.
        </p>

      </div>

      {/* STEP 1 */}
      <Card>
        <div className="flex items-center gap-3">
          <StepPill>STEP 1</StepPill>
          <h2 className="text-lg font-semibold">Choose your university</h2>
        </div>

        <div className="mt-4">
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={loading ? "Loading universities…" : "Search for your university…"}
            disabled={loading}
            className={cx(
              "w-full rounded-xl border px-4 py-3 text-sm shadow-sm focus:outline-none",
              loading
                ? "cursor-not-allowed border-neutral-200 bg-neutral-100 text-neutral-500 dark:border-neutral-800 dark:bg-neutral-900/40 dark:text-neutral-600"
                : "border-neutral-200 bg-white text-neutral-900 placeholder:text-neutral-400 focus:border-neutral-400 dark:border-neutral-800 dark:bg-neutral-950 dark:text-neutral-50 dark:placeholder:text-neutral-500"
            )}
          />
        </div>

        {/* Filters */}
        <div className="mt-3 flex flex-wrap items-center justify-between gap-2">
          {/* NEW: total universities on left */}
          <div className="text-s font-semibold text-neutral-600 dark:text-neutral-400">
            {loading ? "Total universities: —" : `Total universities: ${totalUniversities}`}
          </div>

          {/* Existing controls on right */}
          <div className="flex flex-wrap items-center justify-end gap-2">
            <button
              type="button"
              onClick={() => setSortOrder((v) => (v === "az" ? "none" : "az"))}
              className={cx(
                "rounded-lg border px-3 py-1.5 text-xs font-semibold transition",
                sortOrder === "az"
                  ? "border-neutral-900 bg-neutral-900 text-white dark:border-neutral-200 dark:bg-neutral-200 dark:text-neutral-900"
                  : "border-neutral-200 bg-white text-neutral-700 hover:bg-neutral-50 dark:border-neutral-800 dark:bg-neutral-900 dark:text-neutral-200 dark:hover:bg-neutral-800"
              )}
            >
              A–Z
            </button>

            <button
              type="button"
              onClick={() => setSortOrder((v) => (v === "za" ? "none" : "za"))}
              className={cx(
                "rounded-lg border px-3 py-1.5 text-xs font-semibold transition",
                sortOrder === "za"
                  ? "border-neutral-900 bg-neutral-900 text-white dark:border-neutral-200 dark:bg-neutral-200 dark:text-neutral-900"
                  : "border-neutral-200 bg-white text-neutral-700 hover:bg-neutral-50 dark:border-neutral-800 dark:bg-neutral-900 dark:text-neutral-200 dark:hover:bg-neutral-800"
              )}
            >
              Z–A
            </button>

            <span className="mx-1 h-5 w-px bg-neutral-200 dark:bg-neutral-800" />

            {[
              ["bachelors", "Bachelor’s"],
              ["integrated", "Integrated"],
              ["masters", "Master’s"],
              ["phd", "PhD"],
            ].map(([key, label]) => (
              <button
                key={key}
                type="button"
                onClick={() => setLevelFilter((v) => (v === key ? null : key))}
                className={cx(
                  "rounded-lg border px-3 py-1.5 text-xs font-semibold transition",
                  levelFilter === key
                    ? "border-neutral-900 bg-neutral-50 text-neutral-900 dark:border-neutral-200 dark:bg-neutral-950 dark:text-neutral-50"
                    : "border-neutral-200 bg-white text-neutral-700 hover:bg-neutral-50 dark:border-neutral-800 dark:bg-neutral-900 dark:text-neutral-200 dark:hover:bg-neutral-800"
                )}
              >
                {label}
              </button>
            ))}
          </div>
        </div>

        {loadError && (
          <div className="mt-4 rounded-xl border border-neutral-200 bg-neutral-50 p-3 text-sm text-neutral-700 dark:border-neutral-800 dark:bg-neutral-950 dark:text-neutral-300">
            {loadError}
          </div>
        )}

        {/* Uni list (always shows scrollbar gutter; scroll disabled if not needed) */}
        <div className="mt-3 pt-1">
          <div
            className={cx(
              "h-[250px] pr-1",
              // always show a scrollbar/gutter
              "overflow-y-scroll",
              // if there's nothing to scroll, disable scroll interaction (keeps UI consistent)
              shouldScroll ? "" : "overscroll-none"
            )}
          >
            <div className="grid gap-3 md:grid-cols-2">
              {!loading && filteredUniversities.length === 0 ? (
                <div className="rounded-2xl border border-dashed border-neutral-300 p-5 text-sm text-neutral-600 dark:border-neutral-700 dark:text-neutral-400 md:col-span-2">
                  No universities found.
                </div>
              ) : (
                filteredUniversities.map((u) => {
                  const active = selectedUni?.id === u.id;
                  const subtitle = Array.isArray(u.enabledDegreeTypes)
                    ? `${u.enabledDegreeTypes.length} degree types`
                    : "";

                  const pinned = pinnedIds.has(String(u.id));

                  return (
                    <button
                      key={u.id}
                      type="button"
                      onClick={() => chooseUniversity(u)}
                      className={cx(
                        "rounded-2xl border p-4 text-left transition",
                        active
                          ? "border-neutral-900 bg-neutral-50 dark:border-neutral-200 dark:bg-neutral-950"
                          : "border-neutral-200 bg-white hover:bg-neutral-50 dark:border-neutral-800 dark:bg-neutral-900 dark:hover:bg-neutral-800"
                      )}
                    >
                      <div className="flex items-start justify-between gap-3">
                        <div>
                          <div className="text-sm font-semibold">{u.name}</div>
                          {subtitle ? (
                            <div className="mt-1 text-xs text-neutral-600 dark:text-neutral-400">
                              {subtitle}
                            </div>
                          ) : null}
                        </div>

                        <span className="inline-flex items-center gap-1 rounded-md bg-neutral-200 px-2 py-1 text-xs font-semibold text-neutral-700 dark:bg-neutral-800 dark:text-neutral-200">
                          {pinned ? (
                            <StarIcon className="h-3.5 w-3.5 text-amber-400 fill-amber-400" />
                          ) : null}
                          UK
                        </span>
                      </div>
                    </button>
                  );
                })
              )}
            </div>
          </div>
        </div>
      </Card>

      {/* STEP 2 */}
      <Card>
        <div className="flex items-center gap-3">
          <StepPill>STEP 2</StepPill>
          <h2 className="text-lg font-semibold">Choose your degree level</h2>
        </div>

        <p className="mt-2 text-sm text-neutral-600 dark:text-neutral-400">
          Only available degree types are selectable for the chosen university.
        </p>

        <div className="mt-4 grid gap-3 sm:grid-cols-2">
          {DEGREE_LEVELS.map((lvl) => {
            const available = enabledForUni.includes(lvl.key);
            const disabled = !selectedUni || !available;
            const active = selectedLevel === lvl.key;

            return (
              <button
                key={lvl.key}
                type="button"
                disabled={disabled}
                onClick={() => chooseDegreeLevel(lvl.key)}
                className={cx(
                  "rounded-2xl border p-4 text-left transition",
                  disabled
                    ? "cursor-not-allowed border-neutral-200 bg-neutral-50 text-neutral-400 dark:border-neutral-800 dark:bg-neutral-900/40 dark:text-neutral-600"
                    : active
                    ? "border-neutral-900 bg-neutral-50 dark:border-neutral-200 dark:bg-neutral-950"
                    : "border-neutral-200 bg-white hover:bg-neutral-50 dark:border-neutral-800 dark:bg-neutral-900 dark:hover:bg-neutral-800"
                )}
              >
                <div className="text-sm font-semibold">{lvl.title}</div>
                <div className="mt-1 text-xs text-neutral-600 dark:text-neutral-400">
                  {lvl.subtitle}
                </div>
                {selectedUni && !available ? (
                  <div className="mt-2 text-xs text-neutral-500 dark:text-neutral-500">
                    Coming soon
                  </div>
                ) : null}
              </button>
            );
          })}
        </div>

        {!selectedUni && (
          <div className="mt-3 text-xs text-neutral-500 dark:text-neutral-500">
            Select a university first.
          </div>
        )}
      </Card>

      {/* Continue */}
      <div className="flex items-center justify-end">
        <button
          type="button"
          disabled={!canContinue}
          onClick={continueToCalc}
          className={cx(
            "inline-flex items-center gap-2 rounded-xl px-6 py-3 text-sm font-semibold transition",
            canContinue
              ? "bg-neutral-900 text-white hover:bg-neutral-800 dark:bg-neutral-100 dark:text-neutral-900 dark:hover:bg-white"
              : "cursor-not-allowed bg-neutral-200 text-neutral-500 dark:bg-neutral-800 dark:text-neutral-500"
          )}
        >
          Continue to Calculator →
        </button>
      </div>
    </div>
  );
}